import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeConsultantCianChat,
  closeErrorMessage,
  openAutorisationMotivationConsultantCianChat,
} from '../../actions/consultantCianChat';
import { ConsultantCianChatButton, ConsultantCianChatButtonContainer } from '../../components/ConsultantCianChatButton';
import {
  isOpenSelector,
  isAutorisationPopupOpenSelector,
  openedChatIdSelector,
  isErrorSelector,
  isErrorOpenedSelector,
  isLoadingSelector,
} from '../../selectors/consultantCianChat';
import { ConsultantCianChatPopup } from '../../components/ConsultantCianChatPopup';
import { AutorisationMotivationPopup } from '../../components/AutorisationMotivationPopup';
import { useConsultantCianChat } from '../../hooks/consultantCianChat/useConsultantCianChat';
import { getUser } from '../../selectors/user';
import { ConsultantCianChatOnboarding } from '../ConsultantCianChatOnboarding';
import {
  trackEntryAutorisationPopupOpen,
  trackEntryPointClick,
  trackEntryAutorisationPopupClick,
} from './analytics/analytics';
import { getCurrentJsonQuery } from '../../selectors/location';
import { getFirstGaLabel } from '../../selectors/analytics';
import { ConsultantCianChatErrorPopup } from '../../components/ConsultantCianChatErrorPopup';
import { getRegionId } from '../../selectors/currentRegion';
import { useApplicationContext } from '../../utils/applicationContext';
import { CianChatButtonWithAnimationContainer } from './CianChatButtonWithAnimationContainer';

export const ConsultantCianChat = () => {
  const context = useApplicationContext();
  const isAnimationEnabled = context.config.get('newbuilding-search-frontend.Newbuilding.CianKpnChatAnimation.Enabled');
  const dispatch = useDispatch();
  const isOpened = useSelector(isOpenSelector);
  const openedChatId = useSelector(openedChatIdSelector);
  const frameUrl = `/dialogs/?hostType=frame&type=bot&pageType=ListingJK&chatId=${openedChatId}`;
  const isAutorisationPopupOpen = useSelector(isAutorisationPopupOpenSelector);
  const jsonQuery = useSelector(getCurrentJsonQuery);
  const customLabel = useSelector(getFirstGaLabel);
  const user = useSelector(getUser);
  const isError = useSelector(isErrorSelector);
  const isErrorOpened = useSelector(isErrorOpenedSelector);
  const isLoading = useSelector(isLoadingSelector);
  const regionId = useSelector(getRegionId);
  const { openChat } = useConsultantCianChat();

  const onClick = React.useCallback(() => {
    if (!isLoading) {
      if (!user) {
        dispatch(openAutorisationMotivationConsultantCianChat());
        trackEntryAutorisationPopupOpen({ jsonQuery, label: customLabel, regionId });
      } else {
        openChat();
      }
      trackEntryPointClick({ jsonQuery, regionId });
    }
  }, [customLabel, dispatch, isLoading, jsonQuery, openChat, regionId, user]);

  const onLogInClick = React.useCallback(() => {
    openChat();
    trackEntryAutorisationPopupClick({ jsonQuery, label: customLabel, regionId });
  }, [customLabel, jsonQuery, openChat, regionId]);

  const onClose = React.useCallback(() => {
    dispatch(closeConsultantCianChat());
  }, [dispatch]);

  const onLogInClose = React.useCallback(() => {
    dispatch(closeConsultantCianChat());
  }, [dispatch]);

  const handleErrorClose = React.useCallback(() => {
    dispatch(closeErrorMessage());
  }, [dispatch]);

  return (
    <>
      {!isError && (
        <ConsultantCianChatButtonContainer>
          {isAnimationEnabled ? (
            <CianChatButtonWithAnimationContainer onClick={onClick} />
          ) : (
            <ConsultantCianChatOnboarding>
              <ConsultantCianChatButton isLoading={isLoading} onClick={onClick} />
            </ConsultantCianChatOnboarding>
          )}
        </ConsultantCianChatButtonContainer>
      )}

      <AutorisationMotivationPopup
        isAutorisationPopupOpened={isAutorisationPopupOpen}
        onClick={onLogInClick}
        onClose={onLogInClose}
      />
      {isOpened && <ConsultantCianChatPopup onClose={onClose} url={frameUrl} />}
      {isErrorOpened && <ConsultantCianChatErrorPopup open={isErrorOpened} onClose={handleErrorClose} />}
    </>
  );
};
